'use client';
import React, { useState, useCallback, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import HighlightedHeading from '@/components/molecules/HighlightedHeading';
import { getHighlightPositions } from '@/lib/utils';
import useEmblaCarousel from 'embla-carousel-react';

export type Testimonial = {
  id: string;
  name: string;
  text: string;     // pode conter HTML simples (sem estilos inline)
  image: string;    // URL
  slug?: string;
};

type TestimonialsProps = {
  titleTop?: string;        // “Testemunhos”
  titleBottom?: string;     // “que falam por si”
  description?: string;     // copy à esquerda
  buttonLabel?: string;     // “+ Testemunhos”
  buttonHref?: string;      // rota
  testimonials: Testimonial[];
  className?: string;
};

const ChevronLeft = ({ className = '' }) => (
  <svg viewBox="0 0 11 22" className={className} aria-hidden>
    <path d="M8.72 21.32L.276 11.678a1.44 1.44 0 0 1 0-1.214L8.72.402A1.34 1.34 0 0 1 10.499 1.435c0 .383-.125.718-.376 1.005L3.132 10.875l6.991 8.436c.234.268.351.598.351.991 0 .392-.325.733-.66.733-.334 0-.627-.143-.994-.715Z" fill="#F15A29" />
  </svg>
);

const DoubleChevrons = () => (
  <div className="w-24 h-16 relative">
    <div data-svg-wrapper className="left-[0px] top-0 absolute">
      <svg width="89" height="70" viewBox="0 0 89 70" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 42.0234V2C0 0.895431 0.89543 0 2 0H63.8883C64.4658 0 65.0151 0.249604 65.3949 0.6846L87.8399 26.3914C88.158 26.7557 88.3333 27.2231 88.3333 27.7068V68C88.3333 69.1046 87.4379 70 86.3333 70H23.8852C23.2954 70 22.7357 69.7397 22.3557 69.2886L0.470474 43.312C0.166633 42.9514 0 42.495 0 42.0234Z" fill="white" />
      </svg>
    </div>
    <div data-svg-wrapper className="left-[41.94px] top-[35.28px] absolute">
      <svg width="21" height="15" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.4226 15C11.8247 15.0032 11.3575 14.7156 10.9755 14.2506C10.3805 13.5252 9.82245 12.7615 9.23455 12.0281C8.75884 11.4337 8.25188 10.8697 7.77475 10.2769C7.19395 9.55314 6.63587 8.80697 6.05933 8.07838C5.43877 7.29547 4.80543 6.52853 4.18629 5.74562C3.6907 5.11929 3.21924 4.47059 2.71939 3.84905C2.19681 3.20036 1.65293 2.57562 1.12184 1.93491C0.844926 1.60417 0.580798 1.25905 0.30389 0.92831C0.106504 0.691838 0.00283943 0.351511 -5.96046e-07 -8.98512e-07L10.1107 -4.56558e-07C10.5339 0.504899 10.9386 0.968255 11.3234 1.45398C11.8432 2.10588 12.3473 2.77535 12.8585 3.43683C13.3555 4.08073 13.8454 4.73263 14.3538 5.36375C14.8395 5.96612 15.3549 6.53812 15.8392 7.14049C16.3461 7.77161 16.8233 8.43309 17.3302 9.06422C17.8059 9.65859 18.3143 10.2194 18.79 10.8138C19.3027 11.4561 19.8252 12.0936 20.2868 12.7807C20.8434 13.6067 20.4969 14.6149 19.6023 14.8546C19.3098 14.9329 19.0045 14.9856 18.7034 14.9872C16.6103 14.9984 14.5171 14.9872 12.4226 15Z" fill="var(--orange-2, #F15A29)" />
      </svg>
    </div>
    <div data-svg-wrapper className="left-[41.94px] top-[20.28px] absolute">
      <svg width="21" height="15" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.1177 15L8.26716e-05 15C-0.00418023 14.5526 0.157817 14.0892 0.501704 13.8159C0.598332 13.7392 0.673648 13.6242 0.753224 13.5203C1.30316 12.814 1.84173 12.0981 2.40019 11.4014C2.90749 10.7702 3.44606 10.1726 3.95336 9.54139C4.47488 8.89262 4.96939 8.21508 5.48806 7.56152C6.20568 6.65868 6.93466 5.76702 7.65654 4.87056C8.27468 4.10355 8.89709 3.34132 9.50955 2.56791C9.99696 1.9511 10.4673 1.31671 10.9561 0.699904C11.2986 0.266858 11.7107 -3.86621e-07 12.2535 -3.62893e-07C14.5584 0.00958747 16.8633 0.00159779 19.1696 0.00798971C19.6215 0.00798973 20.0549 0.0910834 20.3406 0.560882C20.6503 1.06903 20.6347 1.80729 20.2326 2.32982C19.7167 2.99936 19.1512 3.62256 18.6183 4.27772C18.3653 4.58773 18.1465 4.93289 17.8964 5.24609C16.9656 6.4094 16.0235 7.56152 15.0942 8.72643C14.5528 9.40716 14.0355 10.1119 13.4912 10.7878C12.9683 11.4382 12.4155 12.055 11.8926 12.7053C11.3057 13.4372 10.7387 14.1882 10.1135 14.9952L10.1177 15Z" fill="var(--orange-3, #F7941D)" />
      </svg>
    </div>
    <div data-svg-wrapper className="left-[26.39px] top-[35.28px] absolute">
      <svg width="21" height="15" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.4128 14.9984C11.882 14.9984 11.4362 14.7938 11.0753 14.3319C9.94579 12.8902 8.80784 11.455 7.66563 10.0245C7.08816 9.30047 6.48663 8.59882 5.90775 7.87479C5.43218 7.28023 4.98351 6.65689 4.50653 6.06393C4.0154 5.45178 3.49313 4.8732 3.00341 4.26105C2.46133 3.58337 1.93905 2.88332 1.40971 2.19286C1.00208 1.66222 0.591625 1.13479 0.192492 0.594563C0.0665245 0.423546 0.00141715 0.210974 1.90735e-06 -8.98512e-07L10.1156 -4.56343e-07C10.4256 0.374 10.7327 0.730419 11.0271 1.10442C11.6004 1.83324 12.158 2.57965 12.7369 3.30367C13.211 3.89824 13.7177 4.46084 14.1933 5.0538C14.7934 5.8066 15.3723 6.58177 15.9682 7.33777C16.5711 8.10335 17.1882 8.85295 17.7912 9.61693C18.2766 10.2339 18.7508 10.8636 19.2292 11.4885C19.6156 11.992 20.0034 12.4939 20.3855 13.0021C20.6332 13.3298 20.5936 13.8508 20.3742 14.2184C20.0147 14.8226 19.508 15.0016 18.9037 15C16.741 14.9952 14.5769 15 12.4128 14.9984Z" fill="var(--orange-2, #F15A29)" />
      </svg>
    </div>
    <div data-svg-wrapper className="left-[26.39px] top-[20.28px] absolute">
      <svg width="21" height="15" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.1188 15L2.41399e-05 15C-0.0013911 14.7922 0.0566572 14.5861 0.177001 14.4183C0.652711 13.7599 1.13408 13.1062 1.62254 12.4606C2.14072 11.775 2.66174 11.091 3.19833 10.423C3.77032 9.71021 4.36921 9.02461 4.94119 8.31185C5.43389 7.69977 5.89686 7.05732 6.38815 6.44524C6.85678 5.86192 7.35656 5.31057 7.82377 4.72566C8.39576 4.0113 8.94226 3.27136 9.50858 2.55061C10.031 1.88739 10.5733 1.24494 11.0886 0.576925C11.3859 0.190179 11.747 0.00319844 12.1901 0.00319846C14.5432 0.00319856 16.8948 0.00959117 19.2465 2.52135e-06C20.0139 -0.00159557 20.6524 0.739934 20.5434 1.45909C20.4967 1.76753 20.3466 2.08556 20.1696 2.33007C19.5905 3.12593 18.9846 3.89623 18.3744 4.66173C17.8321 5.33774 17.2615 5.98658 16.7193 6.66258C16.2195 7.28425 15.7509 7.93629 15.2525 8.55796C14.7386 9.19721 14.1949 9.8029 13.6824 10.4421C13.1316 11.1293 12.6078 11.8437 12.0627 12.5373C11.4836 13.2724 10.8932 13.9964 10.3071 14.7267C10.2505 14.7986 10.1981 14.8769 10.1131 14.9936L10.1188 15Z" fill="var(--orange-3, #F7941D)" />
      </svg>
    </div>
  </div>
);

const TestimonialCard: React.FC<{
  t: Testimonial;
  dim?: boolean;
}> = ({ t, dim = false }) => (
  <Link href={t.slug ? `/testemunhos/${t.slug}` : '#'} className="block">
    <article className="relative w-full h-[450px] sm:h-[500px] md:h-[550px] lg:h-[611px] rounded-sm my-12 sm:my-16 md:my-20 cursor-pointer">
      <Image
        src={t.image || '/event.png'}
        alt={t.name}
        fill
        className="object-cover rounded-sm"
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
      />
      {/* overlay escuro */}
      <div className={`absolute rounded-sm inset-0 ${dim ? 'bg-zinc-800/60' : 'bg-zinc-800/60'}`} />
      {/* nome + excerto */}
      <div className="absolute left-4 sm:left-6 md:left-8 right-4 sm:right-6 md:right-8 bottom-16 sm:bottom-20 md:bottom-24 pointer-events-none">
        <h3 className="text-[#F7941D] text-xl sm:text-2xl md:text-3xl font-bold leading-snug font-[Nunito]">{t.name}</h3>
        <p className="mt-1 sm:mt-2 max-w-xs text-[#F9F7F7] text-sm sm:text-base font-semibold leading-snug font-[Nunito] line-clamp-2"
          dangerouslySetInnerHTML={{ __html: t.text }} />
      </div>
      {/* autocolante com chevrons (CTA) */}
      <div
        aria-label={`Ver testemunho de ${t.name}`}
        className="absolute right-3 sm:right-4 -bottom-6 sm:-bottom-8 scale-75 sm:scale-90 md:scale-100 pointer-events-none"
      >
        <DoubleChevrons />
      </div>
    </article>
  </Link>
);

export const TestimonialsSection: React.FC<TestimonialsProps> = ({
  titleTop = 'Testemunhos',
  titleBottom = 'que Falam por Si',
  description = 'Conheça as histórias de quem já passou por aqui',
  buttonLabel = '+ Testemunhos',
  buttonHref = '/testemunhos',
  testimonials,
  className = '',
}) => {
  const router = useRouter();

  // Paginação simples: 3 por "página" (desktop)
  const [page, setPage] = useState(0);
  const perPage = 3;
  const pages = Math.max(1, Math.ceil(testimonials.length / perPage));
  const slice = testimonials.slice(page * perPage, page * perPage + perPage);

  const prev = () => setPage((p) => (p - 1 + pages) % pages);
  const next = () => setPage((p) => (p + 1) % pages);

  // Embla Carousel para mobile
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    skipSnaps: false,
    dragFree: false
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section className={`relative w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16 ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 grid grid-cols-1 lg:grid-cols-[400px_1fr] xl:grid-cols-[480px_1fr] gap-8 sm:gap-12 lg:gap-16 xl:gap-20 items-start">
        {/* Coluna esquerda: título, copy e botão */}
        <div className="relative flex h-full flex-col justify-center space-y-6 sm:space-y-8 md:space-y-10">
          {
            (() => {
              const sentence = titleTop || "";
              const keywords = titleBottom || "";
              const positions = getHighlightPositions(sentence, keywords);
              return (
                <HighlightedHeading
                  text={sentence}
                  positions={positions}
                  className='max-w-[268px] mb-6 sm:mb-8 md:mb-10'
                />
              );
            })()
          }

          <button
            type="button"
            onClick={() => router.push(buttonHref)}
            className="cursor-pointer rounded-sm border-2 border-[#F15A29] px-5 sm:px-6 py-2.5 sm:py-3 flex md:hidden items-center justify-center gap-2 hover:bg-[#F15A29] hover:text-white transition-colors group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F15A29] w-full sm:w-auto sm:max-w-[200px]"
            aria-label="Ver todos os testemunhos"
          >
            <span className="text-[#F15A29] group-hover:text-white text-xl sm:text-2xl font-medium font-nunito leading-none">+</span>
            <span className="text-[#F15A29] group-hover:text-white text-sm sm:text-base font-medium font-nunito capitalize">
              {buttonLabel.replace('+ ', '')}
            </span>
          </button>

          <div className="text-black text-base sm:text-lg md:text-xl font-light leading-relaxed font-[Nunito] max-w-md" dangerouslySetInnerHTML={{ __html: description }} />

          <button
            type="button"
            onClick={() => router.push(buttonHref)}
            className="cursor-pointer rounded-sm border-2 border-[#F15A29] px-5 sm:px-6 py-2.5 sm:py-3 hidden md:flex items-center justify-center gap-2 hover:bg-[#F15A29] hover:text-white transition-colors group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F15A29] w-full sm:w-auto sm:max-w-[200px]"
            aria-label="Ver todos os testemunhos"
          >
            <span className="text-[#F15A29] group-hover:text-white text-xl sm:text-2xl font-medium font-nunito leading-none">+</span>
            <span className="text-[#F15A29] group-hover:text-white text-sm sm:text-base font-medium font-nunito capitalize">
              {buttonLabel.replace('+ ', '')}
            </span>
          </button>
        </div>

        {/* Coluna direita: cartões */}
        <div className="relative">
          {/* Desktop: Grid com paginação */}
          <div className="hidden xl:grid gap-4 sm:gap-5 md:gap-6 grid-cols-3">
            {slice.map((t) => (
              <TestimonialCard
                key={t.id}
                t={t}
                dim={false}
              />
            ))}
          </div>

          {/* Mobile: Carrossel horizontal */}
          <div className="xl:hidden overflow-hidden -mx-4 px-4" ref={emblaRef}>
            <div className="flex gap-4">
              {testimonials.map((t) => (
                <div key={t.id} className="flex-[0_0_85%] min-w-0">
                  <TestimonialCard
                    t={t}
                    dim={false}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* seta anterior (circular) alinhada com a direita - hidden em mobile */}
          <button
            type="button"
            onClick={prev}
            aria-label="Anterior"
            className="cursor-pointer hidden xl:grid absolute -left-6 xl:-left-9 top-1/2 -translate-y-1/2 w-[48px] h-[48px] sm:w-[58px] sm:h-[58px] rounded-full bg-[#F9F7F7] place-items-center shadow-md"
          >
            <ChevronLeft className="w-[9px] h-[18px] sm:w-[11px] sm:h-[22px]" />
          </button>

          {/* seta seguinte (espelhada) - hidden em mobile */}
          <button
            type="button"
            onClick={next}
            aria-label="Seguinte"
            className="cursor-pointer hidden xl:grid absolute -right-6 xl:-right-9 top-1/2 -translate-y-1/2 w-[48px] h-[48px] sm:w-[58px] sm:h-[58px] rounded-full bg-[#F9F7F7] place-items-center shadow-md rotate-180"
          >
            <ChevronLeft className="w-[9px] h-[18px] sm:w-[11px] sm:h-[22px]" />
          </button>

          {/* Navegação mobile - Indicadores de carrossel */}
          {testimonials.length > 1 && (
            <div className="flex xl:hidden justify-center items-center gap-2 mt-8">
              {scrollSnaps.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => emblaApi?.scrollTo(index)}
                  className={`transition-all duration-300 h-1 rounded-full ${index === selectedIndex
                    ? 'w-16 bg-gradient-to-r from-orange-600 via-orange-500 to-red-500'
                    : 'w-8 bg-gray-300'
                    }`}
                  aria-label={`Ir para slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

    </section >
  );
};
